import logger from "./logger";
/**
 * Maps country codes to currency codes
 * Based on ISO 3166-1 alpha-2 country codes and ISO 4217 currency codes
 */
export const countryCurrencyMap = {
  // Africa
  GH: "GHS", // Ghana
  KE: "KES", // Kenya
  NG: "NGN", // Nigeria
  ZA: "ZAR", // South Africa
  EG: "EGP", // Egypt
  TZ: "TZS", // Tanzania
  UG: "UGX", // Uganda
  RW: "RWF", // Rwanda
  ET: "ETB", // Ethiopia
  MA: "MAD", // Morocco
  DZ: "DZD", // Algeria

  // Americas
  US: "USD", // United States
  CA: "CAD", // Canada
  MX: "MXN", // Mexico
  BR: "BRL", // Brazil
  AR: "ARS", // Argentina

  // Europe
  GB: "GBP", // United Kingdom
  FR: "EUR", // France
  DE: "EUR", // Germany
  IT: "EUR", // Italy
  ES: "EUR", // Spain
  NL: "EUR", // Netherlands
  BE: "EUR", // Belgium
  AT: "EUR", // Austria
  PT: "EUR", // Portugal
  IE: "EUR", // Ireland
  CH: "CHF", // Switzerland
  SE: "SEK", // Sweden
  NO: "NOK", // Norway
  DK: "DKK", // Denmark
  PL: "PLN", // Poland

  // Asia
  CN: "CNY", // China
  JP: "JPY", // Japan
  IN: "INR", // India
  SG: "SGD", // Singapore
  HK: "HKD", // Hong Kong
  KR: "KRW", // South Korea
  TH: "THB", // Thailand
  MY: "MYR", // Malaysia
  ID: "IDR", // Indonesia
  PH: "PHP", // Philippines
  VN: "VND", // Vietnam

  // Middle East
  AE: "AED", // UAE
  SA: "SAR", // Saudi Arabia
  IL: "ILS", // Israel

  // Oceania
  AU: "AUD", // Australia
  NZ: "NZD", // New Zealand
};

/**
 * Get currency code for a country
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {string} ISO 4217 currency code
 */
export const getCurrencyForCountry = countryCode => {
  if (!countryCode) return "KES"; // Default fallback
  const upperCode = countryCode.toUpperCase();
  return countryCurrencyMap[upperCode] || "KES";
};

/**
 * Get currency for a country by country name
 * Matches country name to code, then returns currency
 * @param {string} countryName - Country name
 * @param {Array} countries - Array of country objects with name and code
 * @returns {string} ISO 4217 currency code
 */
export const getCurrencyByCountryName = (countryName, countries = []) => {
  if (!countryName) return "KES";

  const country = countries.find(c => c.name?.toLowerCase() === countryName.toLowerCase());

  if (country?.code) {
    return getCurrencyForCountry(country.code);
  }

  return "KES";
};

/**
 * Get user's preferred currency from localStorage
 * Based on the logged-in user's country
 * Note: This is a non-React utility. For React components, use useUserCurrency hook instead.
 * @param {Array} countries - Array of country objects from Redux/API
 * @returns {string} ISO 4217 currency code
 */
export const getUserCurrency = (countries = []) => {
  try {
    // Check for debug test country first (from test dropdown)
    const debugTestCountry = localStorage.getItem("debug_test_country");
    if (debugTestCountry) {
      const testCountryId = parseInt(debugTestCountry);
      if (countries.length > 0) {
        const testCountry = countries.find(c => c.id === testCountryId);
        if (testCountry?.code) {
          return getCurrencyForCountry(testCountry.code);
        }
      }
    }

    // Check for manually set currency
    const storedCurrency = localStorage.getItem("userCurrency");
    if (storedCurrency) {
      try {
        return JSON.parse(storedCurrency);
      } catch (e) {
        // Ignore parse error
      }
    }

    // Fall back to user's country
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    if (!currentUser?.countryId) {
      return "KES";
    }

    if (countries.length > 0) {
      const userCountry = countries.find(c => c.id === currentUser.countryId);
      if (userCountry?.code) {
        return getCurrencyForCountry(userCountry.code);
      }
    }

    return "KES";
  } catch (error) {
    logger.error("Error getting user currency:", error);
    return "KES";
  }
};
